import {
  getDefaultSocialPostPlatformForType,
  getSocialPostCanvasSize,
  socialPostPlatformSchema,
  socialPostTypeSchema,
  type SocialPostEditableDraft,
  type SocialPostPlatform,
  type SocialPostSceneCanvasV1,
  type SocialPostScenePlanV1,
  type SocialPostType,
} from "@runcheetah/shared";

import { getScheduledPostOutput, resolveCarouselSlidePreview } from "./scheduled-post-preview";

const DEFAULT_CHAT_GENERATED_MEDIA_ASPECT_RATIO = "1080 / 1350";

type ChatGeneratedMediaDimensions = {
  width?: number | null;
  height?: number | null;
};

type ChatGeneratedMediaPostSlot = {
  platform?: string | null;
  postType?: string | null;
};

type ChatGeneratedMediaPost = {
  slot?: ChatGeneratedMediaPostSlot | null;
  draft?: SocialPostEditableDraft | null;
  output?: unknown | null;
} | null;

export type ChatGeneratedAssetAspectRatioInput = ChatGeneratedMediaDimensions & {
  slideId?: string | null;
  slideIndex?: number | null;
  post?: ChatGeneratedMediaPost;
};

export function resolveChatGeneratedAssetAspectRatio(input: ChatGeneratedAssetAspectRatioInput) {
  return (
    resolvePostPlanAspectRatio(input) ??
    resolvePostSlotAspectRatio(input.post?.slot) ??
    resolveDimensionsAspectRatio(input) ??
    DEFAULT_CHAT_GENERATED_MEDIA_ASPECT_RATIO
  );
}

export function resolveChatGeneratedMediaDimensionsAspectRatio(
  input: ChatGeneratedMediaDimensions,
) {
  return resolveDimensionsAspectRatio(input) ?? DEFAULT_CHAT_GENERATED_MEDIA_ASPECT_RATIO;
}

function resolvePostPlanAspectRatio(input: ChatGeneratedAssetAspectRatioInput) {
  const plan = resolvePostPlan(input);
  return plan ? resolveCanvasAspectRatio(plan.scene.canvas) : null;
}

function resolvePostPlan(input: ChatGeneratedAssetAspectRatioInput): SocialPostScenePlanV1 | null {
  const post = input.post;
  if (!post) {
    return null;
  }

  const draftPlan = resolveDraftPlan(post.draft, input.slideId, input.slideIndex);
  if (draftPlan) {
    return draftPlan;
  }

  const output = getScheduledPostOutput(post.output);
  const latestCompleted = output.latestCompleted;
  if (!latestCompleted) {
    return null;
  }

  const carouselPreview = resolveCarouselSlidePreview(
    latestCompleted.carousel,
    input.slideId,
    input.slideIndex,
  );
  return carouselPreview.plan ?? latestCompleted.result?.plan ?? null;
}

function resolveDraftPlan(
  draft: SocialPostEditableDraft | null | undefined,
  slideId: string | null | undefined,
  slideIndex: number | null | undefined,
) {
  if (!draft) {
    return null;
  }

  const slideDraft =
    slideId !== null && slideId !== undefined
      ? draft.carouselSlides.find((slide) => slide.slideId === slideId)
      : slideIndex !== null && slideIndex !== undefined
        ? draft.carouselSlides.find((slide) => slide.slideIndex === slideIndex)
        : null;

  return slideDraft?.plan ?? draft.plan;
}

function resolvePostSlotAspectRatio(slot: ChatGeneratedMediaPostSlot | null | undefined) {
  const postType = parseSocialPostType(slot?.postType);
  if (!postType) {
    return null;
  }

  const platform =
    parseSocialPostPlatform(slot?.platform) ?? getDefaultSocialPostPlatformForType(postType);
  const canvasSize = platform ? getSocialPostCanvasSize(platform, postType) : null;
  return canvasSize ? resolveDimensionsAspectRatio(canvasSize) : null;
}

function resolveCanvasAspectRatio(canvas: SocialPostSceneCanvasV1) {
  return resolveDimensionsAspectRatio(canvas);
}

function resolveDimensionsAspectRatio(input: ChatGeneratedMediaDimensions) {
  return isPositiveFiniteNumber(input.width) && isPositiveFiniteNumber(input.height)
    ? `${input.width} / ${input.height}`
    : null;
}

function parseSocialPostPlatform(value: unknown): SocialPostPlatform | null {
  const parsed = socialPostPlatformSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function parseSocialPostType(value: unknown): SocialPostType | null {
  const parsed = socialPostTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}
